import { useTranslation } from 'react-i18next';
import { useFormatMoney } from '@/components/money/MoneyProvider.js';
import { Badge } from '@/components/ui/badge.js';
import type { BadgeTone } from '@/components/ui/badge.js';
import type { MockBet } from '@/lib/mock-data.js';
import { GAME_ART } from '@/components/lobby/GameCard.js';

const OUTCOME_TONE: Record<MockBet['outcome'], BadgeTone> = {
  win:  'success',
  loss: 'danger',
  push: 'muted',
};

interface BetRowProps {
  bet: MockBet;
  currency?: string;
  hasBorder?: boolean;
}

export function BetRow({ bet, currency = 'THB', hasBorder = true }: BetRowProps) {
  const { t } = useTranslation(['common', 'history']);
  const formatMoney = useFormatMoney();
  const tone = OUTCOME_TONE[bet.outcome];
  const pnl = bet.payoutAmount - bet.betAmount;
  const pnlClass = pnl > 0n ? 'text-[var(--success)]' : pnl < 0n ? 'text-[var(--danger)]' : 'text-[var(--text-muted)]';
  const pnlSign = pnl >= 0n ? '+' : '−';
  const pnlAbs = pnl < 0n ? -pnl : pnl;

  return (
    <div
      className={[
        'grid grid-cols-[40px_1fr_auto] items-center px-4 py-3 gap-3',
        hasBorder ? 'border-t border-[var(--border-subtle)]' : '',
      ]
        .join(' ')
        .trim()}
    >
      {/* Game thumbnail */}
      <div
        className="w-10 h-10 rounded-lg shrink-0"
        style={{ background: GAME_ART[bet.gameType] ?? GAME_ART.slot }}
      />
      {/* Info */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
            {bet.gameName}
          </span>
          <Badge tone={tone}>{t(`outcome.${bet.outcome}`)}</Badge>
        </div>
        <div className="font-mono text-[10px] text-[var(--text-muted)] truncate">
          {bet.providerCode} · {bet.time}
        </div>
        <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 bf-tnum">
          {t('bet.stakeLabel', { ns: 'history' })} {formatMoney(bet.betAmount, currency)}
          {bet.payoutAmount > 0n && ` → ${formatMoney(bet.payoutAmount, currency)}`}
        </div>
      </div>
      {/* P&L */}
      <div className={`font-bold text-sm bf-tnum shrink-0 ${pnlClass}`}>
        {pnlSign} {formatMoney(pnlAbs, currency)}
      </div>
    </div>
  );
}
